import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EuroIcon from '@mui/icons-material/Euro';
import { SelectChangeEvent, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { convertDecimals, roundTwoDecimal } from '../../calculation/util';
import Input from '../../shared/component/Input';
import Table from '../../shared/component/Table';
import { ECurrency, EDrainageTrenchType, ERole } from '../../shared/enum';
import { usePrevious } from '../../shared/hook/hook';
import variable from '../../shared/style/variable';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import {
    currency,
    IPrice,
    primaryChimney,
    primaryChimneyPrice,
    primaryCollector,
    primaryDrainageTrenchPrice,
    secondaryChimney,
    secondaryChimneyPrice,
    secondaryCollector,
    secondaryDrainageTrenchPrice,
    selectPrice,
    setPrice,
    total,
} from '../../store/price/priceSlice';
import { selectResult } from '../../store/result/resultSlice';
import { selectUser } from '../../store/user/userSlice';
import { getMetricCalculationAmountTable, getMetricCalculationCommonTable, getMetricCalculationExcavationTable, getMetricCalculationTotalVolumesTable } from './util';
import { selectProject } from '../../store/project/projectSlice';
import CheckboxCustom from '../../shared/component/Checkbox';
import { selectUtil, setUtil } from '../../store/util/utilSlice';

interface IMetricCalculationProps {}

const Euro = () => <EuroIcon style={{ width: '18px' }} />;
const Dollar = () => <AttachMoneyIcon style={{ width: '18px' }} />;

const MetricCalculation = (props: IMetricCalculationProps) => {
    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);
    const user = useAppSelector(selectUser);
    const price = useAppSelector(selectPrice);
    const util = useAppSelector(selectUtil);

    const prevPrice = usePrevious(price.data);

    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    let output = convertDecimals(result.data!);

    let metricCalculationAmountOutput = getMetricCalculationAmountTable(output.phaseTwoOutputCalculation.metricCalculation);
    let metricCalculationCommonTable = getMetricCalculationCommonTable(output.phaseTwoOutputCalculation.metricCalculation);
    let excavationCalculationOutput = getMetricCalculationExcavationTable(output.phaseTwoOutputCalculation.metricCalculation.excavationCalculation);
    let totalVolumesArea = getMetricCalculationTotalVolumesTable(output.phaseTwoOutputCalculation.metricCalculation.totalVolumes);

    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;
    const hasPrimaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench]?.hasDrainageChimney;
    const hasSecondaryDrainageChimney = project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench]?.hasDrainageChimney;

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value, type, checked } = event.target;
        let isAvailableValue = true;
        if (type == 'checkbox') {
            value = checked;
        } else {
            value = parseFloat(value);
            isAvailableValue = !isNaN(value);
        }
        let updateValue = { [name]: value };
        if (isAvailableValue) {
            let payload: IPrice = { ...price.data!, ...updateValue };
            dispatch(setPrice(payload));
        }
    };

    const getPrice = (price: number, amount: number) => roundTwoDecimal(price * amount);

    React.useEffect(() => {
        if (JSON.stringify(prevPrice) !== JSON.stringify(price.data)) {
            let primaryCollector = getPrice(price.data![primaryDrainageTrenchPrice], result.data?.phaseTwoOutputCalculation.metricCalculation.primaryDrainageTrench.amount!);
            let secondaryCollector = getPrice(price.data![secondaryDrainageTrenchPrice], result.data?.phaseTwoOutputCalculation.metricCalculation.secondaryDrainageTrench.amount!);
            let secondaryChimney = getPrice(price.data![secondaryChimneyPrice], result.data?.phaseTwoOutputCalculation.metricCalculation.secondaryDrainageChimney.amount!);
            let primaryChimney = getPrice(price.data![primaryChimneyPrice], result.data?.phaseTwoOutputCalculation.metricCalculation.primaryDrainageChimney.amount!);
            let total = primaryCollector + secondaryCollector + primaryChimney + secondaryChimney;

            let payload = { ...price.data!, primaryCollector, secondaryCollector, primaryChimney, secondaryChimney, total };
            dispatch(setPrice(payload));
        }
    }, [price.data]);

    return (
        <Stack variant='scrollable' style={{ overflowX: 'auto' }}>
            <Stack minWidth={900} gap={3}>
                <Stack>
                    <Stack paddingBottom={'0.2rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle2' paddingBottom={'0.3rem'} color={variable.greenLight}>
                            {t('metricCalculation')}
                        </Typography>
                        <Stack direction={'row'}>
                            {hasSecondaryDrainageTrench && (
                                <CheckboxCustom
                                    checked={util.data?.drainSymmetric!}
                                    label={t('drainSymmetric')}
                                    handleChange={e => dispatch(setUtil({ ...util.data!, drainSymmetric: e.target.checked }))}
                                />
                            )}
                            {user.data?.role !== ERole.Standard && (
                                <CheckboxCustom
                                    checked={util.data?.metricCalculationTable!}
                                    label={t('printReport')}
                                    handleChange={e => dispatch(setUtil({ ...util.data!, metricCalculationTable: e.target.checked }))}
                                />
                            )}
                        </Stack>
                    </Stack>
                    <Table data={metricCalculationAmountOutput}></Table>
                    <Table data={metricCalculationCommonTable}></Table>
                    <Table data={excavationCalculationOutput}></Table>
                    <Table data={totalVolumesArea}></Table>
                </Stack>
                {user.data?.role != ERole.Standard && (
                    <Stack>
                        <Stack justifyContent={'end'} direction={'row'}>
                            <CheckboxCustom checked={util.data?.priceTable!} label={t('printReport')} handleChange={e => dispatch(setUtil({ ...util.data!, priceTable: e.target.checked }))} />
                        </Stack>
                        <Stack direction={'row'} border={`1px solid ${variable.greenLight2}`}>
                            <Stack direction={'column'} flex={1.5} borderRight={`1px solid ${variable.greenLight2}`}>
                                <Typography style={{ padding: '16px', backgroundColor: variable.green, color: variable.white, flex: 2 }}>{t('inputPrice')}</Typography>
                                <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography flex={4}>{t(currency)}</Typography>
                                    <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                        <Euro />
                                        <Switch color='default' name={currency} onChange={handleChange} aria-label={currency} checked={!!price.data![currency]} />
                                        <Dollar />
                                    </Stack>
                                </Stack>
                                <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography flex={4}>{t(primaryDrainageTrenchPrice, { name: result.data?.phaseTwoOutputCalculation.metricCalculation.primaryDrainageTrench.name })}</Typography>
                                    <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                        <Input id={primaryDrainageTrenchPrice} isNumber name={primaryDrainageTrenchPrice} value={price.data![primaryDrainageTrenchPrice]} onChange={handleChange} />
                                        {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                    </Stack>
                                </Stack>
                                {hasSecondaryDrainageTrench && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>
                                            {t(secondaryDrainageTrenchPrice, { name: result.data?.phaseTwoOutputCalculation.metricCalculation.secondaryDrainageTrench.name })}
                                        </Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input
                                                id={secondaryDrainageTrenchPrice}
                                                isNumber
                                                name={secondaryDrainageTrenchPrice}
                                                value={price.data![secondaryDrainageTrenchPrice]}
                                                onChange={handleChange}
                                            />

                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                {hasPrimaryDrainageChimney && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>{t(primaryChimneyPrice)}</Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input id={primaryChimneyPrice} isNumber name={primaryChimneyPrice} value={price.data![primaryChimneyPrice]} onChange={handleChange} />
                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                {hasSecondaryDrainageChimney && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>{t(secondaryChimneyPrice)}</Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input id={secondaryChimneyPrice} isNumber name={secondaryChimneyPrice} value={price.data![secondaryChimneyPrice]} onChange={handleChange} />
                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                <Typography style={{ paddingLeft: '16px', fontSize: 10, color: variable.black }}>{t('priceInfo')}</Typography>
                            </Stack>
                            <Stack direction={'column'} flex={1.5}>
                                <Typography style={{ padding: '16px', backgroundColor: variable.green, color: variable.white }}>{t('outputPrice')}</Typography>
                                <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography flex={4}>{t(primaryCollector)}</Typography>
                                    <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                        <Input id={primaryCollector} disabled isNumber name={primaryCollector} value={price.data![primaryCollector]} />
                                        {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                    </Stack>
                                </Stack>
                                {hasSecondaryDrainageTrench && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>{t(secondaryCollector)}</Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input id={secondaryCollector} disabled isNumber name={secondaryCollector} value={price.data![secondaryCollector]} />
                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                {hasPrimaryDrainageChimney && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>{t(primaryChimney)}</Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input id={primaryChimney} disabled isNumber name={primaryChimney} value={price.data![primaryChimney]} />
                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                {hasSecondaryDrainageChimney && (
                                    <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <Typography flex={4}>{t(secondaryChimney)}</Typography>
                                        <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                            <Input id={secondaryChimney} disabled isNumber name={secondaryChimney} value={price.data![secondaryChimney]} />
                                            {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                        </Stack>
                                    </Stack>
                                )}
                                <Stack paddingX={'16px'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <Typography flex={4}>{t(total)}</Typography>
                                    <Stack direction={'row'} flex={1.5} alignItems={'center'}>
                                        <Input id={total} disabled isNumber name={total} value={price.data![total]} />
                                        {price.data![currency] == ECurrency.Euro ? <Euro /> : <Dollar />}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default MetricCalculation;
